import { gql } from '@apollo/client';
import type { PageRequest, UpdateDriverFaceClusterRequest, UUID } from '@cobliteam/common-types';
import Alert from '@cobliteam/ui/components/Alert';
import { type Camelized, camelize } from '@cobliteam/utils/camelize';
import type { PageDriverFaceClustersInfo } from '../../__generated__/graphql';
import graphQLRequester from '../../request/GraphQLRequester';
import type { GraphQLRestDataSourceError } from '../../request/Types';
import { toDriverFaceClusterUpdateRequestAPI } from './driverFaceClusterTransformer';

export type GetDriverFaceClusterInfoParams = PageRequest & {
  driverIds?: UUID[];
  hasBeenNominated?: boolean;
  withoutSampleImageUrls?: boolean;
  vehicleIds?: UUID[];
  fromTimestamp?: string;
  toTimestamp?: string;
  samplesPerCluster?: number;
};

export class GraphQLCameraDriverFaceClusterRequester {
  private mutateAssignFaceClusterRequest = gql`
    mutation assignFaceCluster($input: [DriverFaceClusterUpdate]!) {
      assignFaceCluster(input: $input)
    }
  `;

  private mutateRejectClusterSuggestionRequest = gql`
    mutation rejectClusterSuggestion($clusterId: ID!) {
      rejectClusterSuggestion(clusterId: $clusterId)
    }
  `;

  private queryGetDriverFaceClusterInfo = gql`
    query getDriverFaceClusterInfo(
      $page: Int
      $size: Int
      $sort: [String]
      $driverIds: [String]
      $hasBeenNominated: Boolean
      $withoutSampleImageUrls: Boolean
      $vehicleIds: [String]
      $fromTimestamp: DateTime
      $toTimestamp: DateTime
      $samplesPerCluster: Int
    ) {
      driverFaceClustersInfo(
        page: $page
        size: $size
        sort: $sort
        driverIds: $driverIds
        hasBeenNominated: $hasBeenNominated
        withoutSampleImageUrls: $withoutSampleImageUrls
        vehicleIds: $vehicleIds
        fromTimestamp: $fromTimestamp
        toTimestamp: $toTimestamp
        samplesPerCluster: $samplesPerCluster
      ) {
        content {
          created_at
          id
          driver {
            id
            name
            fleet_id
            assigned
            phone_number
            first_license_acquirement_date
            license_expiration_date
            license_category
            driver_code
            score {
              score
            }
          }
          suggested_driver {
            id
            name
            fleet_id
            driver_code
          }
          profile_image_url {
            image_url
          }
          sample_image_urls {
            image_url
          }
          sample_images {
            image_id
            image_url
            captured_at
            vehicle {
              id
              licensePlate
              alias
              groups {
                id
                name
              }
            }
          }
          sample_image_url_count
          suggestion_source
          rejected_at
          updated_at
        }
        empty
        first
        last
        number
        number_of_elements
        size
        total_elements
        total_pages
      }
    }
  `;

  public assignFaceCluster = (driverFaceClusterUpdate: UpdateDriverFaceClusterRequest[]): Promise<boolean> => {
    return graphQLRequester
      .mutate<{
        assignFaceCluster: boolean;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: this.mutateAssignFaceClusterRequest,
        variables: {
          input: toDriverFaceClusterUpdateRequestAPI(driverFaceClusterUpdate),
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }

        return result.assignFaceCluster;
      });
  };

  public rejectClusterSuggestion = (clusterId: string): Promise<boolean> => {
    return graphQLRequester
      .mutate<{
        rejectClusterSuggestion: boolean | null;
        errors?: Array<GraphQLRestDataSourceError>;
      }>({
        mutation: this.mutateRejectClusterSuggestionRequest,
        variables: { clusterId },
      })
      .then((result) => {
        if (result.errors?.length) {
          throw result.errors[0];
        }

        if (result.rejectClusterSuggestion == null) {
          throw new Error('rejectClusterSuggestion returned null');
        }

        return result.rejectClusterSuggestion;
      });
  };

  public getDriverFaceClusterInfo = ({
    page,
    size,
    sort,
    driverIds,
    hasBeenNominated,
    withoutSampleImageUrls,
    vehicleIds,
    fromTimestamp,
    toTimestamp,
    samplesPerCluster,
  }: GetDriverFaceClusterInfoParams): Promise<Camelized<PageDriverFaceClustersInfo>> =>
    graphQLRequester
      .query<{
        driverFaceClustersInfo: PageDriverFaceClustersInfo;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: this.queryGetDriverFaceClusterInfo,
        variables: {
          page,
          size,
          sort: sort ? `createdAt,${sort.order}` : null,
          driverIds,
          hasBeenNominated,
          withoutSampleImageUrls,
          vehicleIds,
          fromTimestamp,
          toTimestamp,
          samplesPerCluster,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          Alert.error('Ops! Não foi possível encontrar grupos de identificação facial. Tente novamente.');

          throw result.errors[0];
        }

        return camelize(result.driverFaceClustersInfo);
      });
}

export default GraphQLCameraDriverFaceClusterRequester;
